import React, { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import { RenderMarkdownWithLatex } from '../../utils/utils'; 
import { AUTH_TOKEN_KEY } from '../../utils/dashboardUtils'; 
import { Dialog, Transition, TransitionChild, DialogPanel } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import OrzelKsiazkaIcon from '../../img/icons/orzel_rece.png'; 
import SolutionStepper from './SolutionStepper'; 

const TrainingProblemsModal = ({ isOpen, onClose, sourceProblemId }) => {
    const [trainingProblems, setTrainingProblems] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Stan do śledzenia, które rozwiązanie jest rozwinięte
    const [expandedProblemId, setExpandedProblemId] = useState(null);

    const apiUrl = process.env.REACT_APP_API_SOURCE || "https://matematly.pl/api/";

    useEffect(() => {
        if (isOpen && sourceProblemId) {
            setLoading(true);
            setExpandedProblemId(null);
            const fetchTraining = async () => {
                const token = localStorage.getItem(AUTH_TOKEN_KEY);
                if (!token) {
                    console.error("Brak tokena");
                    setLoading(false);
                    return;
                }
                
                try {
                    const { data } = await axios.get(
                        `${apiUrl}exams/training/${sourceProblemId}/`,
                        { headers: { Authorization: `Token ${token}` } }
                    );
                    setTrainingProblems(data);
                } catch (error) {
                    console.error("Błąd pobierania zadań:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchTraining();
        }
        if (!isOpen) {
            setTrainingProblems([]);
        }
    }, [isOpen, sourceProblemId, apiUrl]);

    const toggleSolution = (problemId) => {
        setExpandedProblemId(prev => prev === problemId ? null : problemId);
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-50" />
                </TransitionChild>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                
                                <div className="flex items-center justify-center mb-5 text-center">
                                    <img src={OrzelKsiazkaIcon} alt="Orzeł" className="w-16 h-16 mr-3 flex-shrink-0" />
                                    <Dialog.Title as="div" className="text-2xl font-bold font-apotek uppercase tracking-wider text-gray-900">
                                        Szybki Trening
                                    </Dialog.Title>
                                </div>
                                <p className="text-center text-sm text-gray-600 mb-4 border-b pb-4 -mt-2">
                                    Rozwiąż podobne zadania, aby upewnić się, że wszystko dobrze rozumiesz.
                                </p>

                                <div className="mt-2 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                                    {loading ? (
                                        <p className="text-center text-gray-500 py-8">Ładowanie zadań...</p>
                                    ) : trainingProblems.length > 0 ? (
                                        <div className="space-y-6">
                                            {trainingProblems.map((problem, idx) => {
                                                const isExpanded = expandedProblemId === problem.problem_id;
                                                
                                                return (
                                                    <div key={problem.problem_id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:border-gray-300 transition-colors">
                                                        
                                                        {/* USUNIĘTO NAGŁÓWEK "Zadanie X" */}

                                                        {/* Treść Zadania */}
                                                        <div className="text-sm text-gray-800 mb-4 leading-relaxed">
                                                            <RenderMarkdownWithLatex text={problem.question} />
                                                        </div>

                                                        {/* Obrazek */}
                                                        {problem.image_link && (
                                                            <div className="mb-4">
                                                                <img 
                                                                    src={
                                                                        problem.image_link.startsWith('http') 
                                                                        ? problem.image_link 
                                                                        : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${problem.image_link}`
                                                                    } 
                                                                    alt="Trening" 
                                                                    className="rounded border max-w-full h-auto max-h-60 object-contain bg-gray-50" 
                                                                />
                                                            </div>
                                                        )}

                                                        {/* Przycisk Pokaż/Ukryj Rozwiązanie - STANDARDOWY SZARY */}
                                                        <button
                                                            onClick={() => toggleSolution(problem.problem_id)}
                                                            className="w-full text-xs font-bold font-apotek uppercase tracking-widest shadow-sm bg-[#F0F0F0] border border-gray-300 text-gray-600 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors"
                                                        >
                                                            {isExpanded ? 'UKRYJ ROZWIĄZANIE' : 'ROZWIĄZANIE'}
                                                        </button>

                                                        {/* Sekcja Rozwiązania z Animacją */}
                                                        <AnimatePresence>
                                                            {isExpanded && (
                                                                <motion.div
                                                                    initial={{ height: 0, opacity: 0 }}
                                                                    animate={{ height: "auto", opacity: 1 }}
                                                                    exit={{ height: 0, opacity: 0 }}
                                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                                    className="overflow-hidden"
                                                                >
                                                                    <div className="pt-4 border-t border-gray-100 mt-4">
                                                                        {/* STEPPER */}
                                                                        <SolutionStepper solutionText={problem.solution || "Brak rozwiązania."} />
                                                                    </div>
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-center text-gray-500 p-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                                            Brak zadań treningowych dla tego problemu.
                                        </p>
                                    )}
                                </div>

                                <div className="mt-6 pt-4 border-t border-gray-100">
                                    <button
                                        type="button"
                                        className="w-full inline-flex justify-center rounded-lg bg-[#233855] px-4 py-3 text-sm font-apotek tracking-widest text-white shadow-md hover:bg-[#345081] focus:outline-none transition-colors"
                                        onClick={onClose}
                                    >
                                        ZAMKNIJ OKNO
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default TrainingProblemsModal;